import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { Button } from '../components/ui/Button';
/* Same card style as Dashboard: 1px border, 8px radius, no shadow */
const CARD_STYLE: React.CSSProperties = {
  border: '1px solid #E4E4E4',
  borderRadius: '8px'
};
const HEADING_FONT = "'RobotoCondensed VariableFont Wght', Roboto Condensed, sans-serif";
const mockTicket = {
  id: '1',
  codice: 'N7G2UFDK',
  titolo: 'Richiesta informazioni rendicontazione',
  stato: 'Aperto',
  priorita: 'Alta',
  tipologia: 'Informazioni/Assistenza',
  argomento: 'Rendicontazione',
  bando: 'Bando 1 - Nuove Opere',
  richiedente: 'Mario Rossi',
  email: 'mario.rossi@example.com',
  assegnatoA: 'Non assegnato',
  dataCreazione: '15/05/2025 10:30',
  ultimoAggiornamento: '15/05/2025 11:45'
};
const mockMessages = [{
  id: 1,
  sender: 'Mario Rossi',
  role: 'user' as const,
  timestamp: '15/05/2025 10:30',
  content: 'Buongiorno, avrei bisogno di chiarimenti riguardo la documentazione necessaria per la fase di rendicontazione del Bando 1. In particolare, le fatture elettroniche devono avere una dicitura specifica?'
}, {
  id: 2,
  sender: 'Admin Sistema',
  role: 'admin' as const,
  timestamp: '15/05/2025 11:45',
  content: 'Buongiorno Mario. Sì, tutte le fatture elettroniche devono riportare il CUP del progetto e la dicitura "Spesa sostenuta a valere sul bando Per Chi Crea 2025". Trova il manuale completo nella sezione FAQ.'
}, {
  id: 3,
  sender: 'Mario Rossi',
  role: 'user' as const,
  timestamp: '15/05/2025 14:20',
  content: "Grazie per la risposta. Un'altra domanda: per le spese di viaggio è sufficiente la ricevuta del biglietto o serve anche una relazione di missione?"
}];
const StatoBadge = ({
  stato
}: {
  stato: string;
}) => {
  const colors: Record<string, {
    bg: string;
    text: string;
  }> = {
    Aperto: {
      bg: '#e3f2fd',
      text: '#1565c0'
    },
    'In Lavorazione': {
      bg: '#fff3e0',
      text: '#e65100'
    },
    Chiuso: {
      bg: '#e8f5e9',
      text: '#2e7d32'
    }
  };
  const c = colors[stato] || colors['Aperto'];
  return <span style={{
    backgroundColor: c.bg,
    color: c.text,
    borderRadius: '16px',
    fontSize: '12px',
    padding: '2px 10px',
    fontFamily: 'Roboto, sans-serif',
    fontWeight: 500
  }} data-id="element-1792">
      {stato}
    </span>;
};
const PrioritaBadge = ({
  priorita
}: {
  priorita: string;
}) => {
  const colors: Record<string, {
    bg: string;
    text: string;
  }> = {
    Alta: {
      bg: '#fce4ec',
      text: '#c62828'
    },
    Media: {
      bg: '#fff3e0',
      text: '#e65100'
    },
    Bassa: {
      bg: '#e8f5e9',
      text: '#2e7d32'
    }
  };
  const c = colors[priorita] || colors['Media'];
  return <span style={{
    backgroundColor: c.bg,
    color: c.text,
    borderRadius: '16px',
    fontSize: '12px',
    padding: '2px 10px',
    fontFamily: 'Roboto, sans-serif',
    fontWeight: 500
  }} data-id="element-1793">
      {priorita}
    </span>;
};
const aiSuggestions: Record<string, {
  title: string;
  icon: string;
  description: string;
  buttonLabel: string;
  contextLabel: string;
  responses: {
    id: number;
    text: string;
    label: string;
    confidence?: string;
    sources: {
      type: 'ticket' | 'faq' | 'doc';
      id: string;
      title: string;
      excerpt?: string;
    }[];
  }[];
}> = {
  suggest: {
    title: 'Suggerisci Risposta',
    icon: 'tabler:sparkles',
    description: "Risposte generate dall'AI basate sull'analisi di ticket simili già risolti",
    buttonLabel: 'Suggerisci',
    contextLabel: '12 risposte simili',
    responses: [{
      id: 1,
      label: 'Risposta completa',
      confidence: 'Alta pertinenza',
      text: 'Gentile Mario, per le spese di viaggio è necessaria sia la ricevuta/biglietto che una relazione di missione firmata dal legale rappresentante. La relazione deve includere: data, destinazione, motivazione e collegamento al progetto finanziato. Trova il modello nella sezione Modulistica del bando.',
      sources: [{
        type: 'ticket',
        id: '#A3F2',
        title: 'Spese viaggio rendicontazione',
        excerpt: 'Per le spese di viaggio servono ricevuta e relazione di missione...'
      }, {
        type: 'ticket',
        id: '#B7K1',
        title: 'Documentazione missioni',
        excerpt: 'La relazione di missione deve essere firmata dal legale rappresentante...'
      }, {
        type: 'doc',
        id: 'Art.12',
        title: 'Bando 1 — Art. 12 Rendicontazione',
        excerpt: 'Le spese di viaggio richiedono documentazione completa...'
      }]
    }, {
      id: 2,
      label: 'Risposta sintetica',
      confidence: 'Alta pertinenza',
      text: 'Gentile Mario, sì, per le spese di viaggio servono sia la ricevuta che la relazione di missione. Il modello è disponibile nella sezione Modulistica.',
      sources: [{
        type: 'ticket',
        id: '#A3F2',
        title: 'Spese viaggio rendicontazione',
        excerpt: 'Per le spese di viaggio servono ricevuta e relazione di missione...'
      }, {
        type: 'ticket',
        id: '#C9D4',
        title: 'Modello relazione missione',
        excerpt: 'Il modello è disponibile nella sezione Modulistica...'
      }]
    }, {
      id: 3,
      label: 'Risposta con riferimenti',
      confidence: 'Media pertinenza',
      text: "Gentile Mario, come indicato nell'art. 12 del bando, le spese di viaggio richiedono: 1) ricevuta/biglietto originale, 2) relazione di missione (modello M-05) firmata dal legale rappresentante. Può scaricare il modello dalla sezione Modulistica > Rendicontazione.",
      sources: [{
        type: 'doc',
        id: 'Art.12',
        title: 'Bando 1 — Art. 12 Rendicontazione',
        excerpt: 'Le spese di viaggio richiedono: titolo di viaggio, relazione di missione modello M-05...'
      }, {
        type: 'ticket',
        id: '#D2E8',
        title: 'Riferimenti normativi bando',
        excerpt: 'Art. 12 specifica i documenti necessari...'
      }]
    }]
  },
  faq: {
    title: 'Rispondi con FAQ',
    icon: 'tabler:book',
    description: 'Risposte basate sulle FAQ e la documentazione ufficiale del bando',
    buttonLabel: 'Da FAQ',
    contextLabel: '3 FAQ correlate',
    responses: [{
      id: 1,
      label: 'FAQ #12 - Spese di viaggio',
      confidence: 'Alta pertinenza',
      text: 'Per le spese di viaggio è obbligatorio presentare: titolo di viaggio originale (biglietto aereo/ferroviario, ricevuta taxi), relazione di missione compilata e firmata. Si rimanda alla FAQ #12 per maggiori dettagli.',
      sources: [{
        type: 'faq',
        id: 'FAQ #12',
        title: 'Spese di viaggio ammissibili',
        excerpt: 'Sono ammissibili le spese di viaggio documentate con titolo originale e relazione di missione...'
      }]
    }, {
      id: 2,
      label: 'FAQ #15 - Documentazione rendicontazione',
      confidence: 'Media pertinenza',
      text: 'Tutta la documentazione per la rendicontazione, inclusi i modelli per le relazioni di missione, è disponibile nella sezione "Modulistica" del bando. Per le spese di viaggio, fare riferimento alla FAQ #15.',
      sources: [{
        type: 'faq',
        id: 'FAQ #15',
        title: 'Documentazione per rendicontazione',
        excerpt: 'La documentazione completa è disponibile nella sezione Modulistica...'
      }, {
        type: 'faq',
        id: 'FAQ #12',
        title: 'Spese di viaggio ammissibili',
        excerpt: 'Sono ammissibili le spese di viaggio documentate...'
      }]
    }]
  },
  rephrase: {
    title: 'Riformula Risposta',
    icon: 'tabler:refresh',
    description: 'Riformulazioni del testo attuale con toni diversi',
    buttonLabel: 'Riformula',
    contextLabel: '3 varianti',
    responses: [{
      id: 1,
      label: 'Tono formale',
      text: "Egregio Sig. Rossi, in merito alla Sua richiesta, Le comunichiamo che per le spese di viaggio è necessario produrre sia la documentazione attestante il viaggio (biglietto/ricevuta) sia una relazione di missione debitamente sottoscritta dal legale rappresentante dell'ente.",
      sources: []
    }, {
      id: 2,
      label: 'Tono amichevole',
      text: 'Ciao Mario! Per le spese di viaggio ti servono entrambi i documenti: la ricevuta del biglietto e la relazione di missione firmata. Trovi il modello già pronto nella sezione Modulistica del bando. Se hai altri dubbi, siamo qui!',
      sources: []
    }, {
      id: 3,
      label: 'Tono conciso',
      text: 'Sì, servono entrambi: ricevuta del biglietto + relazione di missione firmata dal legale rappresentante. Modello disponibile in Modulistica > Rendicontazione.',
      sources: []
    }]
  }
};
export function TicketDetail() {
  const {
    id
  } = useParams();
  const [replyText, setReplyText] = useState('');
  const [uploadEnabled, setUploadEnabled] = useState(false);
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiModalType, setAiModalType] = useState<string>('suggest');
  const [expandedSources, setExpandedSources] = useState<Record<string, boolean>>({});
  const handleOpenAiModal = (type: string) => {
    setAiModalType(type);
    setAiModalOpen(true);
    setExpandedSources({});
  };
  const handleSelectSuggestion = (text: string) => {
    setReplyText(text);
    setAiModalOpen(false);
  };
  const handleUseDraft = () => {
    setReplyText('Gentile Mario, per le spese di viaggio è necessaria sia la ricevuta/biglietto che una relazione di missione firmata dal legale rappresentante. La relazione deve includere: data, destinazione, motivazione e collegamento al progetto finanziato. Trova il modello nella sezione Modulistica del bando.');
  };
  const toggleSources = (key: string) => {
    setExpandedSources(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };
  const sourceTypeIcon = (type: 'ticket' | 'faq' | 'doc') => {
    switch (type) {
      case 'ticket':
        return 'tabler:message-circle';
      case 'faq':
        return 'tabler:help-circle';
      case 'doc':
        return 'tabler:file-text';
    }
  };
  const sourceTypeColor = (type: 'ticket' | 'faq' | 'doc') => {
    switch (type) {
      case 'ticket':
        return '#2A97DF';
      case 'faq':
        return '#0E74B8';
      case 'doc':
        return '#00497A';
    }
  };
  return <div style={{
    fontFamily: 'Roboto, sans-serif'
  }} className="space-y-4 pb-12" data-id="element-1794">
      {/* Page Header — same style as Dashboard header */}
      <div className="bg-white flex flex-col md:flex-row items-start md:items-center justify-between px-4 md:px-8 py-4 md:py-6 gap-4" style={CARD_STYLE} data-id="element-1795">
        <div className="flex items-center gap-4" data-id="element-1796">
          <Link to="/ticket/tutti" className="flex items-center justify-center hover:bg-[#F5F5F5] transition-colors flex-shrink-0" style={{
          width: '36px',
          height: '36px',
          borderRadius: '50%',
          color: '#80858E',
          textDecoration: 'none'
        }} data-id="element-1797">
            <Icon icon="tabler:arrow-left" fontSize={20} data-id="element-1798" />
          </Link>
          <h1 className="m-0" style={{
          fontSize: '24px',
          fontWeight: 400,
          color: '#293B54',
          fontFamily: 'Roboto, sans-serif'
        }} data-id="element-1799">
            {mockTicket.titolo}
          </h1>
        </div>
      </div>

      {/* Main grid: LEFT widgets, RIGHT conversation */}
      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-4 items-start" data-id="element-1800">
        {/* ── LEFT COLUMN: Info + Actions ── */}
        <div className="static lg:sticky top-[88px] space-y-0" data-id="element-1801">
          <div className="bg-white" style={CARD_STYLE} data-id="element-1802">
            {/* INFORMAZIONI TICKET */}
            <div className="px-6 py-5" data-id="element-1803">
              <h2 style={{
              fontFamily: HEADING_FONT,
              fontSize: '14px',
              fontWeight: 400,
              color: '#293B54',
              margin: '0 0 20px 0',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }} data-id="element-1804">
                Informazioni Ticket
              </h2>

              {/* Assegnato a */}
              <div style={{
              marginBottom: '16px'
            }} data-id="element-1805">
                <div style={{
                fontFamily: 'Roboto, sans-serif',
                fontSize: '13px',
                color: '#80858E',
                marginBottom: '4px'
              }} data-id="element-1806">
                  Assegnato a:
                </div>
                <div className="flex items-center justify-between" data-id="element-1807">
                  <span style={{
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '15px',
                  color: '#293B54',
                  fontWeight: 500
                }} data-id="element-1808">
                    Manuela Capponi
                  </span>
                  <button type="button" style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#80858E',
                  padding: '2px',
                  display: 'flex',
                  alignItems: 'center'
                }} title="Cambia assegnatario" data-id="element-1809">
                    <Icon icon="tabler:chevron-right" fontSize={18} data-id="element-1810" />
                  </button>
                </div>
              </div>

              <div style={{
              borderTop: '1px solid #E4E4E4',
              margin: '16px 0'
            }} data-id="element-1811" />

              {/* Utente */}
              <div style={{
              marginBottom: '20px'
            }} data-id="element-1812">
                <div style={{
                fontFamily: 'Roboto, sans-serif',
                fontSize: '13px',
                color: '#80858E',
                marginBottom: '4px'
              }} data-id="element-1813">
                  Utente:
                </div>
                <div style={{
                fontFamily: 'Roboto, sans-serif',
                fontSize: '15px',
                color: '#293B54',
                fontWeight: 500
              }} data-id="element-1814">
                  Ignazio Ignazi
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex flex-col gap-3" data-id="element-1815">
                <Button variant="outlined" size="md" fullWidth className="uppercase gap-2" data-id="element-1816">
                  <Icon icon="tabler:user" fontSize={16} data-id="element-1817" />
                  Profilo Utente
                </Button>
                <Button variant="outlined" size="md" fullWidth className="uppercase gap-2" data-id="element-1818">
                  <Icon icon="tabler:history" fontSize={16} data-id="element-1819" />
                  Storico Azioni
                </Button>
                <Button variant="outlined" size="md" fullWidth className="uppercase gap-2" data-id="element-1820">
                  <Icon icon="tabler:address-book" fontSize={16} data-id="element-1821" />
                  Contatti
                </Button>
              </div>
            </div>

            {/* AZIONI */}
            <div className="px-6 py-5" style={{
            borderTop: '1px solid #E4E4E4'
          }} data-id="element-1822">
              <h2 style={{
              fontFamily: HEADING_FONT,
              fontSize: '14px',
              fontWeight: 400,
              color: '#293B54',
              margin: '0 0 12px 0',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }} data-id="element-1823">
                Azioni
              </h2>
              <div className="flex flex-col gap-3" data-id="element-1824">
                <Button variant="primary" size="md" fullWidth className="uppercase" data-id="element-1825">
                  Cambia Stato
                </Button>
              </div>
            </div>

            {/* UPLOAD FILE */}
            <div className="px-6 py-5" style={{
            borderTop: '1px solid #E4E4E4'
          }} data-id="element-1826">
              <h2 style={{
              fontFamily: HEADING_FONT,
              fontSize: '14px',
              fontWeight: 400,
              color: '#293B54',
              margin: '0 0 12px 0',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }} data-id="element-1827">
                Upload File
              </h2>
              <label className="flex items-center gap-3 cursor-pointer select-none" data-id="element-1828">
                <button type="button" onClick={() => setUploadEnabled(!uploadEnabled)} style={{
                width: '44px',
                height: '24px',
                borderRadius: '12px',
                backgroundColor: uploadEnabled ? '#2A97DF' : '#E4E4E4',
                position: 'relative',
                transition: 'background-color 0.2s',
                flexShrink: 0,
                border: 'none',
                cursor: 'pointer',
                padding: 0
              }} data-id="element-1829">
                  <div style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  backgroundColor: '#FFFFFF',
                  position: 'absolute',
                  top: '2px',
                  left: uploadEnabled ? '22px' : '2px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                  transition: 'left 0.2s'
                }} data-id="element-1830" />
                </button>
                <span style={{
                fontFamily: 'Roboto, sans-serif',
                fontSize: '14px',
                color: '#293B54'
              }} data-id="element-1831">
                  Consenti upload file
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* ── RIGHT COLUMN: Conversation ── */}
        <div className="bg-white" style={CARD_STYLE} data-id="element-1832">
          {/* Conversation header */}
          <div className="px-4 md:px-8 py-4 md:py-5" style={{
          borderBottom: '1px solid #F5F5F5'
        }} data-id="element-1833">
            <h2 style={{
            fontFamily: 'Roboto Condensed, sans-serif',
            fontSize: '20px',
            fontWeight: 400,
            color: '#293B54',
            margin: 0
          }} data-id="element-1834">
              Conversazione
            </h2>
          </div>

          {/* Messages */}
          <div className="px-4 md:px-8 py-4 md:py-6 space-y-6" data-id="element-1835">
            {mockMessages.map(msg => <div key={msg.id} className={`flex gap-3 ${msg.role === 'admin' ? 'flex-row-reverse' : ''}`} data-id="element-1836">
                <div className="flex-shrink-0 flex items-center justify-center" style={{
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              backgroundColor: msg.role === 'admin' ? '#2A97DF' : '#293B54',
              color: 'white',
              fontWeight: 500,
              fontSize: '14px',
              fontFamily: 'Roboto, sans-serif'
            }} data-id="element-1837">
                  {msg.sender.charAt(0)}
                </div>
                <div className={`flex flex-col max-w-[80%] ${msg.role === 'admin' ? 'items-end' : ''}`} data-id="element-1838">
                  <div style={{
                padding: '14px 18px',
                fontSize: '14px',
                fontFamily: 'Roboto, sans-serif',
                lineHeight: '1.6',
                borderRadius: msg.role === 'admin' ? '16px 4px 16px 16px' : '4px 16px 16px 16px',
                backgroundColor: msg.role === 'admin' ? '#F5F5F5' : '#FFFFFF',
                color: '#444444',
                border: msg.role === 'admin' ? 'none' : '1px solid #E4E4E4'
              }} data-id="element-1839">
                    {msg.content}
                  </div>
                </div>
              </div>)}
          </div>

          {/* Reply */}
          <div className="px-4 md:px-8 py-4 md:py-6 border-t border-[#E4E4E4]" data-id="element-1840">
            {/* AI Suggestion Buttons — pill-shaped with context */}
            <div className="flex flex-wrap items-center gap-2 mb-3" data-id="element-1841">
              {Object.entries(aiSuggestions).map(([key, val]) => <button key={key} type="button" onClick={() => handleOpenAiModal(key)} className="hover:bg-[#F5F5F5] transition-colors" style={{
              padding: '6px 14px',
              borderRadius: '9999px',
              border: '1px solid #E4E4E4',
              backgroundColor: 'transparent',
              color: '#293B54',
              fontSize: '13px',
              fontFamily: HEADING_FONT,
              cursor: 'pointer',
              textTransform: 'uppercase',
              letterSpacing: '0.3px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px'
            }} data-id="element-1842">
                  <Icon icon={val.icon} fontSize={14} style={{
                color: '#293B54'
              }} data-id="element-1843" />
                  <span data-id="element-1844">{val.buttonLabel}</span>
                  <span style={{
                fontSize: '11px',
                color: '#80858E',
                fontFamily: 'Roboto, sans-serif',
                textTransform: 'none',
                letterSpacing: '0'
              }} data-id="element-1845">
                    · {val.contextLabel}
                  </span>
                </button>)}
            </div>

            <textarea rows={3} className="w-full focus:outline-none resize-none" placeholder="Scrivi una risposta..." value={replyText} onChange={e => setReplyText(e.target.value)} style={{
            padding: '14px 18px',
            border: '1px solid #E4E4E4',
            borderRadius: '4px',
            fontSize: '16px',
            fontFamily: 'Roboto, sans-serif',
            color: '#444444'
          }} data-id="element-1846" />
            <div className="flex items-center justify-end mt-3" data-id="element-1847">
              <Button variant="primary" size="md" className="flex items-center gap-2 w-full sm:w-auto justify-center" disabled={!replyText.trim()} data-id="element-1848">
                <Icon icon="tabler:send" fontSize={16} data-id="element-1849" />
                INVIA RISPOSTA
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* AI Suggestions Modal */}
      {aiModalOpen && <div className="fixed inset-0 flex items-center justify-center p-4" style={{
      zIndex: 1000,
      backgroundColor: 'rgba(30, 30, 30, 0.5)'
    }} onClick={() => setAiModalOpen(false)} data-id="element-1850">
          <div className="bg-white w-full max-w-[90vw] md:w-[640px] max-h-[90vh] overflow-hidden flex flex-col" style={{
        borderRadius: '8px',
        boxShadow: 'rgba(47,43,61,0.16) 0px 4px 11px'
      }} onClick={e => e.stopPropagation()} data-id="element-1851">
            {/* Modal Header */}
            <div className="flex items-center justify-between" style={{
          padding: '20px 24px',
          borderBottom: '1px solid #E4E4E4'
        }} data-id="element-1852">
              <div className="flex items-center gap-3" data-id="element-1853">
                <Icon icon={aiSuggestions[aiModalType]?.icon || 'tabler:sparkles'} fontSize={20} style={{
              color: '#293B54',
              flexShrink: 0
            }} data-id="element-1854" />
                <div data-id="element-1855">
                  <h3 style={{
                fontFamily: HEADING_FONT,
                fontSize: '18px',
                fontWeight: 400,
                color: '#293B54',
                margin: 0
              }} data-id="element-1856">
                    {aiSuggestions[aiModalType]?.title}
                  </h3>
                  <p style={{
                fontFamily: 'Roboto, sans-serif',
                fontSize: '13px',
                color: '#80858E',
                margin: '2px 0 0 0'
              }} data-id="element-1857">
                    {aiSuggestions[aiModalType]?.description}
                  </p>
                </div>
              </div>
              <button type="button" onClick={() => setAiModalOpen(false)} className="flex items-center justify-center hover:bg-[#F5F5F5] transition-colors" style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            border: 'none',
            backgroundColor: 'transparent',
            color: '#80858E',
            cursor: 'pointer'
          }} data-id="element-1858">
                <Icon icon="tabler:x" fontSize={18} data-id="element-1859" />
              </button>
            </div>

            {/* Modal Tabs */}
            <div className="flex items-end overflow-x-auto" style={{
          padding: '0 24px',
          borderBottom: '1px solid #E4E4E4'
        }} data-id="element-1860">
              {Object.entries(aiSuggestions).map(([key, val]) => <button key={key} type="button" onClick={() => {
            setAiModalType(key);
            setExpandedSources({});
          }} className="whitespace-nowrap" style={{
            padding: '14px 20px 12px',
            fontFamily: HEADING_FONT,
            fontSize: '14px',
            letterSpacing: '0.2px',
            border: 'none',
            borderTop: 'none',
            borderLeft: 'none',
            borderRight: 'none',
            borderBottom: aiModalType === key ? '2px solid #2A97DF' : '2px solid transparent',
            backgroundColor: 'transparent',
            color: aiModalType === key ? '#293B54' : '#AAAAAA',
            fontWeight: aiModalType === key ? 500 : 400,
            cursor: 'pointer',
            marginBottom: '-1px',
            outline: 'none'
          }} data-id="element-1861">
                  {val.title}
                </button>)}
            </div>

            {/* Modal Body — Suggestions list */}
            <div style={{
          padding: '16px 24px 24px',
          overflowY: 'auto',
          flex: 1
        }} data-id="element-1862">
              <div className="space-y-3" data-id="element-1863">
                {aiSuggestions[aiModalType]?.responses.map(resp => {
              const sourceKey = `${aiModalType}-${resp.id}`;
              const isExpanded = expandedSources[sourceKey] || false;
              const hasSources = resp.sources && resp.sources.length > 0;
              return <div key={resp.id} className="hover:border-[#2A97DF] transition-colors" style={{
                border: '1px solid #E4E4E4',
                borderRadius: '8px',
                padding: '16px'
              }} data-id="element-1864">
                      <div className="flex items-center justify-between mb-2" data-id="element-1865">
                        <div className="flex items-center gap-2" data-id="element-1866">
                          <span style={{
                      fontFamily: HEADING_FONT,
                      fontSize: '13px',
                      fontWeight: 500,
                      color: '#293B54',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px'
                    }} data-id="element-1867">
                            {resp.label}
                          </span>
                          {resp.confidence && <span style={{
                      fontSize: '11px',
                      fontFamily: 'Roboto, sans-serif',
                      color: resp.confidence === 'Alta pertinenza' ? '#2e7d32' : '#e65100',
                      backgroundColor: resp.confidence === 'Alta pertinenza' ? '#e8f5e9' : '#fff3e0',
                      padding: '2px 8px',
                      borderRadius: '9999px',
                      fontWeight: 500
                    }} data-id="element-1868">
                              {resp.confidence}
                            </span>}
                        </div>
                        <button type="button" onClick={() => handleSelectSuggestion(resp.text)} style={{
                    padding: '4px 14px',
                    borderRadius: '9999px',
                    border: '1px solid #293B54',
                    backgroundColor: 'transparent',
                    color: '#293B54',
                    fontSize: '12px',
                    fontFamily: HEADING_FONT,
                    cursor: 'pointer',
                    textTransform: 'uppercase',
                    letterSpacing: '0.3px'
                  }} data-id="element-1869">
                          Usa
                        </button>
                      </div>
                      <p style={{
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                  lineHeight: '1.6',
                  color: '#444444',
                  margin: 0
                }} data-id="element-1870">
                        {resp.text}
                      </p>

                      {/* Expandable Sources */}
                      {hasSources && <div style={{
                  marginTop: '10px'
                }} data-id="element-1871">
                          <button type="button" onClick={() => toggleSources(sourceKey)} className="hover:bg-[#F5F5F5] transition-colors" style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '5px',
                    padding: '4px 10px',
                    borderRadius: '9999px',
                    border: 'none',
                    backgroundColor: isExpanded ? '#F5F5F5' : 'transparent',
                    color: '#80858E',
                    fontSize: '12px',
                    fontFamily: 'Roboto, sans-serif',
                    cursor: 'pointer'
                  }} data-id="element-1872">
                            <Icon icon="tabler:link" fontSize={13} data-id="element-1873" />
                            <span data-id="element-1874">Vedi fonti ({resp.sources.length})</span>
                            <Icon icon={isExpanded ? 'tabler:chevron-up' : 'tabler:chevron-down'} fontSize={13} data-id="element-1875" />
                          </button>

                          {isExpanded && <div style={{
                    marginTop: '10px',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px'
                  }} data-id="element-1876">
                              {resp.sources.map((src, idx) => <div key={idx} style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '7px',
                      padding: '7px 12px',
                      backgroundColor: '#FFFFFF',
                      border: '1px solid #E4E4E4',
                      borderRadius: '6px'
                    }} data-id="element-1877">
                                  <Icon icon={sourceTypeIcon(src.type)} fontSize={14} style={{
                        color: '#80858E',
                        flexShrink: 0
                      }} data-id="element-1878" />
                                  <span style={{
                        fontFamily: 'Roboto, sans-serif',
                        fontSize: '12px',
                        color: '#80858E',
                        whiteSpace: 'nowrap'
                      }} data-id="element-1879">
                                    {src.id}
                                  </span>
                                  <span style={{
                        fontFamily: 'Roboto, sans-serif',
                        fontSize: '12px',
                        fontWeight: 500,
                        color: '#293B54',
                        whiteSpace: 'nowrap'
                      }} data-id="element-1880">
                                    {src.title}
                                  </span>
                                </div>)}
                            </div>}
                        </div>}
                    </div>;
            })}
              </div>
            </div>
          </div>
        </div>}
    </div>;
}